import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment";
import { Wrapper } from "./styles";
import { ControlForm, Modal } from "../../../../components";
import { Controller, useForm } from "react-hook-form";
import InputForm from "../../../../components/controls/InputForm";
import {
  DATETIME_FORMAT3,
  DATETIME_FORMAT4
} from "../../../../config/constants";
import { TagInput } from "../../../WorkflowManagement/components/TagInput/TagInput";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ICreateMeetingScheduleReq,
  IMeetingRoom,
  IMeetingSchedule
} from "../../../../models/meeting";
import { useApi } from "../../../../hooks/useApi.hook";
import MeetingService from "../../../../services/api/meeting.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import {
  EMeetingRoomStatus,
  meetingRoomStatusColor,
  meetingRoomStatusTitle
} from "../../../../models/common/models.enum";
import ScheduleCard from "../ScheduleCard";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import variableStyles from "../../../../theme/variable-styles";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

type Props = {
  isCreate: boolean;
  refetch: Function;
  myBookings: IMeetingSchedule[];
};

const BookingHeader = ({ refetch, myBookings, isCreate }: Props) => {
  const { t, i18n } = useTranslation();
  const now = moment().add(5, "minutes").format(DATETIME_FORMAT3);
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [endDateTime, setEndDateTime] = useState<any>(null);
  const [beginDateTime, setBeginDateTime] = useState<any>(now);
  const meetingRoom = useApi<IMeetingRoom[]>({ isFetch: true });
  const createMeetingSchedule = useApi<IMeetingSchedule>({});
  const [locale, setLocale] = useState<string>();

  useEffect(() => {
    setLocale(i18n.language);
    dayjs.locale(locale);
  }, [i18n.language]);

  const validationSchema = Yup.object().shape({
    Title: Yup.string().required(
      t("Meeting.ValidationTitleRequired") as string
    ),
    Description: Yup.string().required(
      t("Meeting.ValidationDescriptionRequired") as string
    ),
    RoomId: Yup.string()
      .notOneOf([""], t("Meeting.ValidationRoomRequired") as string)
      .required(t("Meeting.ValidationRoomRequired") as string),
    MeetingParticipants: Yup.array()
      .required(t("Meeting.ValidationParticipantsRequired") as string)
      .min(1, t("Meeting.ValidationParticipantsRequired") as string)
  }) as any;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ICreateMeetingScheduleReq>({
    defaultValues: {
      StartDate: now
    },
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = (request: ICreateMeetingScheduleReq) => {
    const participants = request.MeetingParticipants.map((meeting: any) =>
      Object.assign({
        ["meetingParticipantId"]: meeting.id,
        ["userNameParticipant"]: meeting.userName,
        ["type"]: meeting.type
      })
    );

    const req = {
      ...request,
      StartDate: beginDateTime,
      EndDate: endDateTime,
      MeetingParticipants: participants
    };
    createMeetingSchedule.request(
      MeetingService.create(req).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message: t("Meeting.ToastCreateSuccess")
          });
          refetch();
          setOpenCreate(false);
          reset();
        }
      })
    );
  };

  return (
    <Wrapper>
      <Box flex={"0 0 auto"}>
        {isCreate && (
          <div>
            <Button
              variant="contained"
              size="small"
              aria-label="aria-modal"
              onClick={() => {
                meetingRoom.request(
                  MeetingService.getRooms({
                    CurrentPage: 1,
                    PageSize: 10
                  })
                );
                setOpenCreate(true);
              }}
              sx={{
                margin: "20px",
                width: "calc(100% - 40px)",
                backgroundColor: variableStyles.MainColor
              }}
            >
              <Typography className="subtitle2-text" variant="subtitle2">
                {t("Meeting.CreateButton")}
              </Typography>
            </Button>

            <Modal
              title={t("Meeting.CreateModalTitle")}
              textSubmit={t("Meeting.CreateModalSubmit")}
              textClose={t("Meeting.CreateModalCancel")}
              open={openCreate}
              onSubmit={handleSubmit(onSubmit)}
              onClose={() => {
                setOpenCreate(false);
                reset();
              }}
              loadingState={createMeetingSchedule.loading}
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}
              >
                <ControlForm
                  title={t("Meeting.TitleLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <InputForm
                    placeholder={t("Meeting.TitlePlaceholder")}
                    required
                    name="Title"
                    errors={errors}
                    control={control}
                    size="small"
                  />
                </ControlForm>
                <ControlForm
                  title={t("Meeting.DescriptionLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <InputForm
                    placeholder={t("Meeting.DescriptionPlaceholder")}
                    name="Description"
                    required
                    errors={errors}
                    control={control}
                    size="small"
                  />
                </ControlForm>

                <ControlForm
                  title={t("Meeting.RoomLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <Controller
                    name="RoomId"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        disabled={createMeetingSchedule.loading}
                        fullWidth
                        size="small"
                        placeholder={t("Meeting.RoomPlaceholder")}
                        sx={{ fontSize: "small", height: "40px" }}
                        error={!!errors.RoomId}
                        defaultValue=""
                      >
                        {meetingRoom.data &&
                          meetingRoom.data?.map(room => (
                            <MenuItem
                              key={room.id}
                              value={room.id}
                              sx={{ fontSize: "small" }}
                            >
                              {room.roomName}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  />
                  {errors.RoomId && (
                    <Typography
                      variant="caption"
                      marginLeft="16px"
                      color="error"
                    >
                      {t("Meeting.RoomErrorText")}
                    </Typography>
                  )}
                </ControlForm>
                <ControlForm
                  title={t("Meeting.StartTimeLabel")}
                  classname="brand-form"
                >
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="StartDate"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      defaultValue={moment(beginDateTime)}
                      sx={{ width: "100%" }}
                      onChange={e => {
                        const date = e ? e.format(DATETIME_FORMAT3) : now;
                        setBeginDateTime(date);
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EndTimeLabel")}
                  classname="brand-form"
                >
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="EndDate"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      sx={{ width: "100%" }}
                      onChange={e => {
                        const date = e ? e.format(DATETIME_FORMAT3) : null;
                        setEndDateTime(date);
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                </ControlForm>

                <ControlForm
                  title={t("Meeting.ParticipantsLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <TagInput
                    name={"MeetingParticipants"}
                    control={control}
                    errors={errors}
                    isError={!!errors.MeetingParticipants}
                    placeholder={t("Meeting.ParticipantsPlaceholder")}
                  />
                </ControlForm>

                <ControlForm
                  title={t("Meeting.OtherParticipantsLabel")}
                  classname="brand-form"
                  infor={t("Meeting.OtherParticipantsInfo")}
                >
                  <InputForm
                    placeholder={t("Meeting.OtherParticipantsPlaceholder")}
                    name="otherMeetingParticipants"
                    errors={errors}
                    control={control}
                    size="small"
                  />
                </ControlForm>
              </form>
            </Modal>
          </div>
        )}

        <Divider />
        <Typography variant="subtitle2" color={"#7E969F"} margin="4px 24px">
          {t("Meeting.FilterSectionTitle")}
        </Typography>
        <Divider />

        <Box
          className="detail-color"
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            margin: "0 24px"
          }}
        >
          {Object.values(EMeetingRoomStatus)
            .filter(v => !isNaN(Number(v)))
            .map((status, i) => (
              <Box key={i} display="flex" margin="12px 0" alignItems="center">
                <CircleRoundedIcon
                  sx={{
                    fontSize: 16,
                    marginRight: "12px",
                    color: meetingRoomStatusColor(status as EMeetingRoomStatus)
                      .color
                  }}
                />
                <Typography variant="caption" color={"#2D393E"}>
                  {t(meetingRoomStatusTitle(status as EMeetingRoomStatus))}
                </Typography>
              </Box>
            ))}
        </Box>
        <Divider />
        <Typography variant="subtitle2" color={"#7E969F"} margin="4px 24px">
          {t("Meeting.MyBookingsTitle")}
        </Typography>
        <Divider />
      </Box>
      <Box
        flex={"1 1 auto"}
        sx={{
          overflow: "auto",
          padding: "0 12px",
          marginTop: "12px"
        }}
      >
        {myBookings.map(item => (
          <ScheduleCard key={item.id} item={item} />
        ))}
      </Box>
    </Wrapper>
  );
};

export default BookingHeader;
